import { useState } from 'react';

import { Check, ChevronDown, Copy, HelpCircle, Settings2, Shield } from 'lucide-react';

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

import { cn } from '@/lib/utils';

import type { UIField } from '@optidev/shared-types';

import type { FieldRow, InstructionRow } from './connection-form-layout';

/**
 * Presentational pieces of the connection form layout.
 *
 * Each one exists to take content that used to cost a permanent line in the form and give it a
 * place: the firewall IP moves above the fields (it is a prerequisite, not an input), the helper
 * text collects into one disclosure, and optional fields collapse behind a counted "Advanced".
 */

/** Uppercase section header — "Connection", "Authentication". */
export function SectionHeader({ title }: { title: string }): React.ReactElement {
  return (
    <p className="text-[10.5px] font-bold uppercase tracking-[0.09em] text-muted-foreground">
      {title}
    </p>
  );
}

/**
 * The firewall IP as a prerequisite strip above the form.
 *
 * It is reference information rather than an input, and it has to be acted on before the
 * credentials will work at all — so it belongs before the fields, not stranded between them.
 */
export function PrerequisiteIpStrip({
  egressIps,
  className,
}: {
  egressIps: string[];
  className?: string;
}): React.ReactElement | null {
  const [copied, setCopied] = useState(false);

  if (!egressIps || egressIps.length === 0) return null;

  const handleCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(egressIps.join('\n'));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy egress IPs:', err);
    }
  };

  const isSingle = egressIps.length === 1;

  return (
    <div
      className={cn(
        'flex items-center gap-2 rounded-lg border border-primary/25 bg-primary/[0.06] px-3 py-2',
        className,
      )}
    >
      <Shield className="mt-0.5 size-3.5 flex-shrink-0 self-start text-primary" />
      <p className="min-w-0 text-xs leading-snug">
        Allow{' '}
        <code className="font-mono font-semibold tabular-nums">{egressIps.join(', ')}</code> in your
        database firewall first
      </p>
      <button
        type="button"
        onClick={handleCopy}
        className="ml-auto flex flex-shrink-0 items-center gap-1 self-start pt-px text-[11px] font-semibold text-primary hover:underline"
      >
        {copied ? <Check className="size-3" /> : <Copy className="size-3" />}
        {copied ? 'Copied' : isSingle ? 'Copy' : 'Copy all'}
      </button>
    </div>
  );
}

/**
 * Every line of per-field instruction, in one place instead of five.
 *
 * The wording is unchanged — it is read once, during first setup, but costs a line forever when
 * it sits under each input. Grouped, it also reads as a single set of directions.
 */
export function InstructionsDisclosure({
  instructions,
  title,
  className,
}: {
  instructions: InstructionRow[];
  title: string;
  className?: string;
}): React.ReactElement | null {
  const [open, setOpen] = useState(false);

  if (instructions.length === 0) return null;

  return (
    <Collapsible open={open} onOpenChange={setOpen} className={className}>
      <div className="overflow-hidden rounded-lg border border-border">
        <CollapsibleTrigger className="flex w-full items-center gap-2 bg-muted/30 px-3 py-2 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted/60">
          <HelpCircle className="size-3.5 flex-shrink-0" />
          {title}
          <ChevronDown
            className={cn('ml-auto size-3.5 transition-transform', open && 'rotate-180')}
          />
        </CollapsibleTrigger>
        <CollapsibleContent>
          {/*
            Rows start under the header's text, not under its icon (px-3 + icon 14px + gap 8px),
            so the panel reads as one left edge. The label column is sized to the longest label
            rather than a fixed width, which kept short labels stranded from their text.
          */}
          <dl className="grid gap-x-4 gap-y-2 border-t border-border py-2.5 pl-[34px] pr-3 sm:grid-cols-[minmax(0,max-content)_1fr]">
            {instructions.map((row) => (
              <div key={`${row.label}-${row.text}`} className="contents">
                {row.label && (
                  <dt className="text-xs font-semibold leading-relaxed">{row.label}</dt>
                )}
                <dd
                  className={cn(
                    'whitespace-pre-line text-xs leading-relaxed text-muted-foreground',
                    !row.label && 'sm:col-span-2',
                  )}
                >
                  {row.text}
                </dd>
              </div>
            ))}
          </dl>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

/**
 * Optional fields, collapsed with their count on the header.
 *
 * Closed is not hidden: the count and the field labels say what is in there, and anything that
 * already has a value opens the group automatically so nothing set is ever out of sight.
 */
export function AdvancedDisclosure({
  labels,
  defaultOpen = false,
  children,
  className,
}: {
  labels: string[];
  defaultOpen?: boolean;
  children: React.ReactNode;
  className?: string;
}): React.ReactElement {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <Collapsible open={open} onOpenChange={setOpen} className={className}>
      <div className="overflow-hidden rounded-lg border border-border">
        <CollapsibleTrigger className="flex w-full items-center gap-2 bg-muted/30 px-3 py-2 text-left text-xs font-medium text-muted-foreground transition-colors hover:bg-muted/60">
          <Settings2 className="size-3.5 flex-shrink-0" />
          <span className="truncate">
            Advanced
            {labels.length > 0 && (
              <span className="text-muted-foreground/70"> — {labels.join(', ')}</span>
            )}
          </span>
          <span className="ml-auto flex flex-none items-center gap-1.5">
            <span className="text-[10.5px] tabular-nums text-muted-foreground/70">
              {labels.length} optional
            </span>
            <ChevronDown className={cn('size-3.5 transition-transform', open && 'rotate-180')} />
          </span>
        </CollapsibleTrigger>
        <CollapsibleContent>
          {/* Same left edge as the header's text, matching the instructions panel */}
          <div className="space-y-4 border-t border-border py-3 pl-[34px] pr-3">{children}</div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

/**
 * Renders packed field rows: one field full width, or a narrow field (port, catalog, schema)
 * paired with its neighbour on a wide dialog and stacked on mobile.
 */
export function FieldRows({
  rows,
  renderField,
  className,
}: {
  rows: FieldRow[];
  renderField: (field: UIField) => React.ReactNode;
  className?: string;
}): React.ReactElement {
  return (
    <div className={cn('space-y-4', className)}>
      {rows.map((row) => {
        const key = row.map((field) => field.name).join('+');
        if (row.length === 1) {
          return <div key={key}>{renderField(row[0])}</div>;
        }
        return (
          <div key={key} className="grid gap-4 sm:grid-cols-2">
            {row.map((field) => (
              <div key={field.name}>{renderField(field)}</div>
            ))}
          </div>
        );
      })}
    </div>
  );
}
